import { type Project_portfolioProject$key } from '#app/__generated__/relay/Project_portfolioProject.graphql.js';
import { type Project_userProfile$key } from '#app/__generated__/relay/Project_userProfile.graphql.js';
import { ApprovalFilter } from '#app/components/ApprovalFilter/ApprovalFilter.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { AvatarWithDetails } from '#app/components/Avatars/AvatarWithDetails/AvatarWithDetails.js';
import { BackToProfile } from '#app/components/BackToProfile/BackToProfile.js';
import { Link } from '#app/components/Link/index.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { ProjectRolesToolsOrganizations } from '#app/components/ProjectRolesToolsOrganizations/ProjectRolesToolsOrganizations.js';
import { RichText } from '#app/components/RichTextEditor/RichText.js';
import { ShareButton } from '#app/components/ShareButton/ShareButton.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useRegisterAnalyticEvent } from '#app/hooks/useRegisterAnalyticEvent.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import {
  fullHeight,
  hide,
  noHeight,
  show,
  springAnimation,
} from '#app/utilities/animation.js';
import { formatName } from '#app/utilities/formatName.js';
import { AuthorBlock } from '../AuthorBlock/AuthorBlock.js';
import { ProjectLayout } from '../ProjectLayout/ProjectLayout.js';
import { ProjectExpandedCollaboratorsList } from './ProjectExpandedCollaboratorsList/ProjectExpandedCollaboratorsList.js';
import { ProjectMinimizedCollaboratorsList } from './ProjectMinimizedCollaboratorsList/ProjectMinimizedCollaboratorsList.js';
import { ProjectPartnerWithAction } from './ProjectPartnerWithAction/ProjectPartnerWithAction.js';
import { CustomEditorContent } from '@contra/editor';
import { EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';

type ProjectProps = {
  readonly projectRef: Project_portfolioProject$key;
  readonly userProfileRef: Project_userProfile$key;
};

const transition = {
  height: {
    damping: 30,
    stiffness: 200,
    type: 'spring',
  },
  opacity: {
    damping: 30,
    stiffness: 250,
    type: 'spring',
  },
};

export const Project = ({ projectRef, userProfileRef }: ProjectProps) => {
  const isMobile = useIsMobile();

  const [areCollaboratorsExpanded, setAreCollaboratorsExpanded] =
    useState(false);

  const project = useFragment<Project_portfolioProject$key>(
    graphql`
      fragment Project_portfolioProject on PortfolioProject {
        __typename
        title
        analyticsId
        contributionYear
        collaborators {
          count
        }
        id
        title
        contributionYear
        visitorCanEdit
        blocknoteContent
        publishedAt
        roles {
          edges {
            node {
              name
            }
          }
        }
        tools {
          edges {
            node {
              name
              logoImage {
                url
                uid
              }
            }
          }
        }
        slug
        author {
          id
          displayUsername
          firstName
          lastName
          title
          avatarImage {
            id
            uid
            url
          }
        }

        ...ProjectMinimizedCollaboratorsList_portfolioProject
        ...ProjectExpandedCollaboratorsList_portfolioProject
        ...ProjectRolesToolsOrganizations_portfolioProject
      }
    `,
    projectRef,
  );

  const profile = useFragment<Project_userProfile$key>(
    graphql`
      fragment Project_userProfile on UserProfile {
        id
        firstName
        analyticsId
        displayUsername
        visitorCanEdit
        lastName
        professionalTitle
        avatarImage {
          id
          uid
          url
        }
        ...ProjectPartnerWithAction_userProfile
      }
    `,
    userProfileRef,
  );

  const { publishedAt, visitorCanEdit } = project;

  useRegisterAnalyticEvent(
    !visitorCanEdit && project.id
      ? {
          eventType: 'PortfolioProjectView',
          input: { portfolioProjectId: project.id },
        }
      : undefined,
  );

  const [urlSearchParameters] = useURLParameters('/p/@slug');
  const justPublished = urlSearchParameters.justPublished;
  const { track } = useAnalytics();

  const {
    avatarImage,
    displayUsername,
    firstName,
    lastName,
    professionalTitle,
  } = profile;

  const isBlog = Boolean(project.author);
  const hasCollaborators = project.collaborators.count > 0;
  const isDraft = publishedAt === null;

  return (
    <ProjectLayout>
      <ProjectLayout.Card>
        <ProjectLayout.ToothbrushContainer>
          {visitorCanEdit && !isMobile && (
            <ProjectLayout.EditButton>
              <Link
                aria-label="Edit project"
                onClick={() =>
                  track('button', {
                    action: 'tapped',
                    button_name: 'edit_portfolio_project',
                    portfolio_project_id: project.analyticsId,
                  })
                }
                to={getRoute('/p/@slug/edit', { args: { slug: project.slug } })}
              >
                <EditIcon size={24} />
              </Link>
            </ProjectLayout.EditButton>
          )}
          {!justPublished && !isDraft ? (
            <ShareButton
              analyticsProperties={{
                is_owner: profile.visitorCanEdit,
                owner_id: profile.analyticsId,
                portfolio_project_id: project.analyticsId,
              }}
              toastSuccessMessage="The project link was successfully copied to your clipboard."
              url={`https://contra.com${getRoute('/p/@slug', {
                args: { slug: project.slug },
              })}`}
            />
          ) : null}
        </ProjectLayout.ToothbrushContainer>
        <ProjectLayout.Content>
          <ProjectLayout.Details addTopPadding={false}>
            {isMobile ? (
              <BackToProfile
                analyticsProperties={{
                  is_owner: project.visitorCanEdit,
                  portfolio_project_id: project.analyticsId,
                }}
                to={getRoute('/@username', {
                  args: {
                    username: profile.displayUsername,
                  },
                })}
              >
                {isBlog ? 'Back to Blogs' : 'View All Projects'}
              </BackToProfile>
            ) : null}

            <ProjectLayout.Title>{project.title}</ProjectLayout.Title>

            {isBlog ? null : (
              <ProjectLayout.Participants>
                <ProjectLayout.MinimizedContainer
                  hasCollaborators={hasCollaborators}
                >
                  {isMobile && hasCollaborators ? (
                    <Avatar
                      avatarImage={avatarImage}
                      size="sm"
                    />
                  ) : (
                    <AvatarWithDetails
                      avatarImage={avatarImage}
                      displayUsername={displayUsername}
                      size="lg"
                      userName={formatName(
                        { firstName, lastName },
                        'fullName',
                        '',
                      )}
                    />
                  )}

                  <ApprovalFilter>
                    <AnimatePresence mode="wait">
                      {areCollaboratorsExpanded ? (
                        isMobile ? (
                          <animated.div
                            animate={show}
                            exit={hide}
                            initial={hide}
                            key="userDetails"
                            transition={springAnimation.veryFast}
                          >
                            <ProjectLayout.OwnerDetails>
                              <ProjectLayout.OwnerName>
                                {formatName(
                                  { firstName, lastName },
                                  'fullName',
                                )}
                              </ProjectLayout.OwnerName>
                              <ProjectLayout.OwnerTitle>
                                {professionalTitle}
                              </ProjectLayout.OwnerTitle>
                            </ProjectLayout.OwnerDetails>
                          </animated.div>
                        ) : null
                      ) : (
                        <animated.div
                          animate={show}
                          exit={hide}
                          initial={hide}
                          key="collaborators"
                          transition={springAnimation.veryFast}
                        >
                          <ProjectMinimizedCollaboratorsList
                            collaboratorsRef={project}
                            onSeeMore={() => setAreCollaboratorsExpanded(true)}
                          />
                        </animated.div>
                      )}
                    </AnimatePresence>
                  </ApprovalFilter>
                </ProjectLayout.MinimizedContainer>

                <ProjectLayout.ExpandedContainer>
                  <ApprovalFilter>
                    <AnimatePresence mode="wait">
                      {areCollaboratorsExpanded && (
                        <animated.div
                          animate={{ ...fullHeight, ...show }}
                          exit={{ ...noHeight, ...hide }}
                          initial={{ ...noHeight, ...hide }}
                          key="expanded"
                          transition={transition}
                        >
                          <ProjectExpandedCollaboratorsList
                            collaboratorsRef={project}
                            onSeeLess={() => setAreCollaboratorsExpanded(false)}
                          />
                        </animated.div>
                      )}
                    </AnimatePresence>
                  </ApprovalFilter>
                </ProjectLayout.ExpandedContainer>
              </ProjectLayout.Participants>
            )}

            <ProjectRolesToolsOrganizations projectRef={project} />
          </ProjectLayout.Details>

          <ProjectLayout.Editor>
            {isBlog && project.author ? (
              <ProjectLayout.Author>
                <AuthorBlock
                  showAuthorTitle
                  {...project.author}
                />
              </ProjectLayout.Author>
            ) : null}
            <RichText
              content={project.blocknoteContent as CustomEditorContent}
            />
          </ProjectLayout.Editor>

          {project.contributionYear && (
            <>
              <ProjectLayout.ThematicBreak />
              <ProjectLayout.Year>
                {project.contributionYear}
              </ProjectLayout.Year>
            </>
          )}
        </ProjectLayout.Content>

        {isMobile && (
          <ProjectLayout.MobileActions>
            {visitorCanEdit && (
              <ProjectLayout.EditButtonMobile>
                <Link
                  aria-label="Edit project"
                  onClick={() =>
                    track('button', {
                      action: 'tapped',
                      button_name: 'edit_portfolio_project',
                      portfolio_project_id: project.analyticsId,
                    })
                  }
                  to={getRoute('/p/@slug/edit', {
                    args: { slug: project.slug },
                  })}
                >
                  <EditIcon size={24} />
                </Link>
              </ProjectLayout.EditButtonMobile>
            )}
            <ShareButton
              analyticsProperties={{
                is_owner: profile.visitorCanEdit,
                owner_id: profile.analyticsId,
                portfolio_project_id: project.analyticsId,
              }}
              toastSuccessMessage="The project link was successfully copied to your clipboard."
              url={`https://contra.com${getRoute('/p/@slug', {
                args: { slug: project.slug },
              })}`}
            />
          </ProjectLayout.MobileActions>
        )}
      </ProjectLayout.Card>
      {isBlog ? null : (
        <ProjectLayout.PartnerWithAction>
          <ProjectPartnerWithAction profileRef={profile} />
        </ProjectLayout.PartnerWithAction>
      )}
    </ProjectLayout>
  );
};
